import { Grid, Typography } from '@mui/material';
import DocumentDeathForm from 'civil-status/components/document-death-form';
import { DocumentSearchDeathCrumbs } from 'civil-status/constants/breadcrumb';
import createSchema from 'civil-status/utils/create-schema-death';
import {
  transformDocumentDeath,
  transformDocumentDeathRequestUpdate,
} from 'civil-status/utils/transform-document-death';
import { DocumentDeathFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { useFormik } from 'formik';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentDeathCM } from 'shell/models/document-death';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import DescriptionForm from 'civil-status/components/shared/description-document';
import ButtonsForm from 'civil-status/components/shared/buttons';

const typeAct = 'D';

export default function DocumentDeathFormPage(): React.ReactElement {
  const params = useParams();

  const { userProfile } = useUserProfile();

  const [death] = useHttpFetch<HttpResponse<DocumentDeathCM>, DocumentDeathFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/visualizzazione-atto-morte/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentDeath,
    }
  );

  const substituteImage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/sostituzione-immagine-atto-morte`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const updateDeath = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/modifica-atto-morte`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const validationSchema = useMemo(() => {
    return createSchema(mode);
  }, [mode]);

  const formikForm = useFormik<DocumentDeathFormVM>({
    initialValues: (death.data as DocumentDeathFormVM) || {
      annotationNational: '',
      noteNational: '',
      annotationInternational: '',
      noteInternational: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values: DocumentDeathFormVM, formContext) => {
      // If act substitution mode
      if (mode === PageMode.Substitution) {
        (
          substituteImage({
            data: {
              // TODO MAPPING, to test
              attoMorteForUpload: {
                numeroRegistro: values?.registerNumber || undefined,
                anno: values?.year || undefined,
                numeroAtto: values?.documentNumber || undefined,
                parte: values?.portion || undefined,
                serie: `${values?.serie || ' '}${values?.serie2 || ''}` || undefined,
                matricola: values?.matricola || undefined,
                cognome: values?.surname || undefined,
                nome: values?.name || undefined,
                idOperatore: userProfile.datiLogin?.key?.userName || undefined,
                //nomePadre: values?.nameFather,
              },
              file: values.uploadFile?.rawTiff?.split(',')[1] || undefined,
              filename: values.uploadFile?.file?.name || undefined,
              mimeType: values.uploadFile?.file?.type || undefined,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue(
            'indicatorUpdate',
            'È necessario effettuare la convalida dei dati.'
          );
        });
      }

      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentDeathRequestUpdate(values);
        (
          updateDeath({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue('indicatorUpdate', '');
        });
      }
    },
  });

  const unlockDocument = useHttpClient(`${__CIVIL_STATUS_API_URL__}/atti-morte-sima/sblocca-atto`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
            },
          },
        });
      }
    };
  }, []);

  const deleteAll = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi/all`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const handleDeleteAllInternational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'INT',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationInternationalList', []);
      setMode(PageMode.Detail);
    });
  }, [deleteAll, formikForm.values, typeAct]);

  const handleDeleteAllNational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'NAZ',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationNationalList', []);
      setMode(PageMode.Detail);
    });
  }, [formikForm.values, typeAct, deleteAll]);

  // visibility
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    setVisibility(!!formikForm.values?.indicatorUpdate);
  }, [formikForm.values?.indicatorUpdate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Breadcrumbs
        items={[
          ...DocumentSearchDeathCrumbs,
          { name: 'Atto di Morte', path: '/stato-civile/morte/ricerca/dettaglio' },
        ]}
        base="stato-civile"
      />
      <form onSubmit={formikForm.handleSubmit}>
        <Grid container spacing={2}>
          <Grid container spacing={2} item xs={12} md={4}>
            <Grid item xs={12} md={6}>
              <Typography
                color="secondary.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di Morte{' '}
                {mode === PageMode.International ? '- Annotazioni Internazionali' : ''}
                {mode === PageMode.National ? '- Annotazioni Italia' : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <DescriptionForm values={formikForm.values} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <ButtonsForm
              mode={mode}
              onAnnotationInternationalMode={() => {
                setMode(PageMode.International);
              }}
              onAnnotationNationalMode={() => {
                setMode(PageMode.National);
              }}
              onDetailMode={() => {
                setMode(PageMode.Detail);
              }}
              onEditMode={() => {
                setMode(PageMode.Edit);
              }}
              onPrintMode={() => {
                setMode(PageMode.Print);
              }}
              onSubstitutionMode={() => {
                setMode(PageMode.Substitution);
              }}
              onAnnotationInternationalDeleteAll={handleDeleteAllInternational}
              onAnnotationNationalDeleteAll={handleDeleteAllNational}
              url="/stato-civile/morte/ricerca"
            />
          </Grid>
        </Grid>
        <SearchMessages
          type={MessageType.Warning}
          title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
          messages={
            formikForm.values?.indicatorUpdate
              ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
              : []
          }
          visibility={visibility}
          onChangeVisibility={() => setVisibility(false)}
        />
        <DocumentDeathForm
          onChange={formikForm.setFieldValue}
          onChanges={onChanges}
          values={formikForm.values}
          errors={formikForm.errors}
          touched={formikForm.touched}
          mode={mode}
          type={typeAct}
          setMode={setMode}
        />
      </form>
    </>
  );
}

DocumentDeathFormPage.displayName = 'home page component';
